import React, { useState } from 'react';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Colors } from 'library';
import { Divider, Link, Modal, Row, Stack, Text } from 'library/ui';
import { formatAddress } from 'shared';
import { format12HourTime, formatLongWeekdayMonthDayYear } from 'shared/Dates';

import type { SubPoolEvent } from '../types';

function formatWhen(event: SubPoolEvent): string {
    return `${formatLongWeekdayMonthDayYear(event.start_date)} at ${format12HourTime(event.start_date)}`;
}

function EventDetails({ event }: { event: SubPoolEvent }) {
    return (
        <Stack gap="sm">
            <Text weight="bold">{event.title}</Text>
            <Text size="sm" tone="secondary">
                {formatWhen(event)}
            </Text>
            {event.description && <Text size="sm">{event.description}</Text>}
            {event.link && (
                <Link href={event.link} size="sm" wrap icon={<OpenInNewIcon style={{ fontSize: 14 }} />}>
                    Join the event
                </Link>
            )}
            {!event.link && event.address && (
                <Row gap="sm" align="center">
                    <PlaceOutlinedIcon style={{ fontSize: 16, color: Colors.textSecondary }} />
                    <Text size="sm" tone="secondary">
                        {formatAddress(event.address, false)}
                    </Text>
                </Row>
            )}
        </Stack>
    );
}

type EventsView = 'next' | 'all';

export function SubPoolEventsSummary({ events, poolName }: { events: SubPoolEvent[]; poolName: string }) {
    const [view, setView] = useState<EventsView | null>(null);
    const nextEvent = events[0];

    if (!nextEvent) return null;

    const remaining = events.length - 1;
    const shownEvents = view === 'all' ? events : [nextEvent];

    const open = (next: EventsView) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setView(next);
    };

    return (
        <Stack gap="none">
            <Row gap="sm" align="center">
                <EventOutlinedIcon style={{ fontSize: 16, color: Colors.textSecondary }} />
                <Text size="sm" weight="semibold" tone="secondary">
                    Upcoming Event
                </Text>
                <Link size="sm" wrap onClick={open('next')}>
                    {`${nextEvent.title} — ${formatWhen(nextEvent)}`}
                </Link>
            </Row>
            {remaining > 0 && (
                <Row gap="sm" align="center">
                    <Text size="sm" tone="secondary">
                        {`+ ${remaining} more upcoming ${remaining === 1 ? 'event' : 'events'}`}
                    </Text>
                    <Link size="sm" onClick={open('all')}>
                        View all
                    </Link>
                </Row>
            )}

            {view && (
                <Modal
                    title={view === 'all' ? `${poolName} Events` : 'Upcoming Event'}
                    subtitle="Trainings, info sessions, and other events for your substitute pool."
                    onClose={() => setView(null)}
                    maxHeight="80vh"
                >
                    <Stack gap="md">
                        {shownEvents.map((event, index) => (
                            <Stack gap="md" key={event.id}>
                                {index > 0 && <Divider />}
                                <EventDetails event={event} />
                            </Stack>
                        ))}
                    </Stack>
                </Modal>
            )}
        </Stack>
    );
}
